import React from 'react';
import PropTypes from 'prop-types';

import DraftMessage from '../../correspondances/DraftMessage';

const SmsResponseView = (props) => {
  const {communicationTarget, isRegenerating, onApproveAndSendSuccess, onDiscard, onRegenerate, requester, response} = props;

  const [isSendingSms, setIsSendingSms] = React.useState(false);
  const [smsData, setSmsData] = React.useState(() => {
    return {
      message: response.message,
      from: [requester?.firstName, requester?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim(),
      delayUntil: 0,
    };
  });

  const relationships = communicationTarget?.relationships || [];
  const [selectedRelationship, setSelectedRelationship] = React.useState(communicationTarget?.defaultRelationship || null);
  const recipientSms = communicationTarget?.getRelationshipSms?.(selectedRelationship) || '';

  const updateSmsData = (key) => (value) => {
    setSmsData((prevSmsData) => ({
      ...prevSmsData,
      [key]: value,
    }));
  };

  React.useEffect(() => {
    setSmsData({
      message: response.message,
      from: [requester?.firstName, requester?.lastName]
          .filter(Boolean)
          .join(' ')
          .trim(),
      delayUntil: 0,
    });
  }, [requester, response]);

  const canApproveAndSend = Boolean(recipientSms) && Boolean(communicationTarget?.sendSms);

  const handleApproveAndSend = React.useCallback(async () => {
    if (!canApproveAndSend) {
      return;
    }

    setIsSendingSms(true);

    try {
      await communicationTarget.sendSms(smsData, requester, selectedRelationship?.relationshipRn);
      await onApproveAndSendSuccess?.();
      onDiscard?.();
    } finally {
      setIsSendingSms(false);
    }
  }, [canApproveAndSend, communicationTarget, onApproveAndSendSuccess, onDiscard, requester, selectedRelationship?.relationshipRn, smsData]);

  const fields = [
    {
      key: 'to',
      type: 'relationshipAutocomplete',
      expression: 'skills.SkillsModule.Sms.Fields.To',
      getOptionDetail: communicationTarget.getRelationshipSms,
      options: relationships,
      selectedRelationship,
      setSelectedRelationship,
      autocompleteSx: {
        '& .MuiAutocomplete-endAdornment': {
          right: 0,
        },
      },
    },
    {
      key: 'from',
      type: 'editable',
      expression: 'skills.SkillsModule.Sms.Fields.From',
      value: smsData.from,
      onChange: updateSmsData('from'),
    },
  ];

  const actions = [
    {
      key: 'approve',
      variant: 'primary',
      expression: 'skills.SkillsModule.Sms.Actions.ApproveAndSend',
      disabled: isSendingSms || !canApproveAndSend,
      onClick: handleApproveAndSend,
    },
    {
      key: 'regenerate',
      variant: 'secondary',
      expression: 'skills.SkillsModule.Sms.Actions.Regenerate',
      disabled: isRegenerating,
      onClick: onRegenerate,
    },
    {
      key: 'discard',
      variant: 'tertiary',
      expression: 'skills.SkillsModule.Sms.Actions.Discard',
      onClick: onDiscard,
    },
  ];

  return (
    <DraftMessage
      titleExpression='skills.SkillsModule.Sms.Draft'
      fields={fields}
      bodyField={{
        value: smsData.message,
        onChange: updateSmsData('message'),
      }}
      actions={actions}
    />
  );
};

SmsResponseView.propTypes = {
  communicationTarget: PropTypes.object,
  isRegenerating: PropTypes.bool,
  onApproveAndSendSuccess: PropTypes.func,
  onDiscard: PropTypes.func,
  onRegenerate: PropTypes.func,
  requester: PropTypes.object.isRequired,
  response: PropTypes.shape({
    message: PropTypes.string,
  }),
};

export default React.memo(SmsResponseView);
